import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { ViewPatientCases } from '@view/view_patient_cases/mod';
import { Outlet, Route, Routes } from 'react-router-dom';
import { CellulaProviderWrapper } from './cellula_provider_wrapper.component';
import { sharedResolution } from '../../src/shared_resolution';
import { Root, createRoot } from 'react-dom/client';
import { ProgrammaticBrowserRouter } from '@cortex-shim/programmatic_browser_router.component';
import { Bleed, Flex, Grid } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import styled from 'styled-components';
import { UniversalConfig } from '@application/universal_config.service';
import { TerribleAngularRedirectFix_DO_NOT_USE } from '@public/src/components/terrible_angular_redirect_fix_do_not_use.component';

const containerElementName = 'reactVisitsComponent';

@Component({
	standalone: false,
	selector: 'visits-component',
	template: `
		<semantic-view>
			<header-component header></header-component>
			<div class="wrapper-container withSafeArea" #${containerElementName}></div>
			<footer-component footer></footer-component>
		</semantic-view>
	`,
	encapsulation: ViewEncapsulation.None,
	styles: [
		`
			& .withSafeArea {
				width: 100%;
				max-width: 1320px;
				margin: 0 auto 40px;
			}
		`,
	],
})
export class VisitsWrapperComponent implements OnDestroy, AfterViewInit {
	@ViewChild(containerElementName, { static: false }) containerRef: ElementRef;

	private root: Root;

	constructor(private readonly ngZone: NgZone) {
		// Empty
	}

	ngAfterViewInit() {
		this.render();
	}

	ngOnDestroy() {
		this.root?.unmount();
	}

	private async render() {
		const universalConfigService = sharedResolution.resolve(UniversalConfig);

		const root = createRoot(this.containerRef.nativeElement);
		this.root = root;

		this.ngZone.runOutsideAngular(() => {
			root.render(
				<CellulaProviderWrapper useGlobals>
					<ProgrammaticBrowserRouter basename={universalConfigService.alias} forceNewHistory>
						<Routes>
							<Route
								path='visits/*'
								element={
									<ViewPatientCases.Root>
										<Routes>
											<Route element={<PageOutlet />}>
												<Route path='/' element={null} />
												<Route
													path='/video/:callticket'
													element={
														<StyledAppointmentWrapper>
															<ViewPatientCases.AppointmentCase />
														</StyledAppointmentWrapper>
													}
												/>
												<Route
													path='/message/:messageId'
													element={
														<StyledMessagingWrapper>
															<ViewPatientCases.MessagingCase />
														</StyledMessagingWrapper>
													}
												/>
												<Route path='/*' element={<ViewPatientCases.ViewNotFound />} />
											</Route>
										</Routes>
									</ViewPatientCases.Root>
								}
							/>
							<Route path='*' element={<TerribleAngularRedirectFix_DO_NOT_USE />} />
						</Routes>
					</ProgrammaticBrowserRouter>
				</CellulaProviderWrapper>,
			);

			this.root = root;
		});
	}
}

const StyledAppointmentWrapper = styled.div`
	background-color: ${tokens.color.bg.surface};
	width: 100%;
	height: 100%;
	overflow-x: hidden;
	margin-top: 40px;
	position: relative;

	${tokens.breakpointQuery.medium} {
		width: 100%;
		height: auto;
		border-radius: 16px;
		border: 1px solid ${tokens.color.border.default};
	}
`;

const StyledMessagingWrapper = styled.div`
	display: flex;
	flex-direction: column;
	background-color: ${tokens.color.bg.surface};
	width: 100%;
	height: 100%;
	overflow-x: hidden;
	margin-top: 40px;
	position: relative;

	${tokens.breakpointQuery.medium} {
		width: 100%;
		height: 605px;
		border-radius: 16px;
		border: 1px solid ${tokens.color.border.default};
	}
`;

function PageOutlet() {
	return (
		<Flex
			gap={tokens.spacing.x2}
			style={{
				margin: `${tokens.size.x5} auto 0`,
				padding: `0 ${tokens.spacing.x2}`,
			}}
		>
			<ViewPatientCases.Heading bleed={false} />

			<Grid
				areas={{
					base: [
						// prettier-ignore
						'cases',
						'infoView',
					],
					M: [
						// prettier-ignore
						'cases  infoView',
					],
				}}
				columns={{
					base: ['4fr'],
					M: ['5fr', '7fr'],
				}}
				gap={{
					base: tokens.spacing.x2,
					M: `${tokens.spacing.x5} ${tokens.spacing.x3}`,
				}}
			>
				<Flex gridArea='cases'>
					<Bleed bleedAmount='16px'>
						<ViewPatientCases.ListRoot />
					</Bleed>
				</Flex>
				<Flex gridArea='infoView'>
					<Outlet />
				</Flex>
			</Grid>
		</Flex>
	);
}
